import { FC, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react/macro';
import { Container, Form, Segment } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { QRCodeSVG } from 'qrcode.react';
import { Context } from '../../../context/ContextProvider';
import {
  associateTotp,
  enableTotp,
  getSessionUser,
  isTotpEnabled,
  MFA_CODE_FORMAT_ERROR,
  MFA_CODE_PATTERN,
  mfaErrorhandle,
} from '../../../Repository/Cognito';
import DefaultFormInput from '../../Molecules/DefaultFormInput';
import Loading from '../../Atoms/Loading';
import DefaultButton from '../../Atoms/button/DefaultButton';
import ErrorMassage from '../../Atoms/ErrorMessage';
import ComfirmdDalog from '../../Atoms/ComfirmdDalog ';
import SegmentHeader from '../../Atoms/setting/SegmentHeader';
import SettingText from '../../Atoms/setting/SettingText';
import { URL } from '../../../util';

const ISSUER = 'GrantDrive';

const MfaSetupForm: FC = () => {
  const { userName } = useContext(Context);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [disable, setDisable] = useState(false);
  const [open, setOpen] = useState(false);
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);

  useEffect(() => {
    document.title = 'GrantDrive | お客様ポータル | 2段階認証設定';
    getSessionUser()
      .then(async user => {
        setCognitoUser(user);
        // 設定済みの場合はシークレットを再発行しない(URL直接アクセスによる再設定を防ぐ)
        if (await isTotpEnabled(user)) {
          setAlreadyEnabled(true);
          setLoading(false);

          return;
        }
        setSecret(await associateTotp(user));
        setLoading(false);
      })
      .catch(() => {
        setErr('エラーが発生しました。しばらくしてからお試しください。');
        setLoading(false);
      });
  }, []);

  const otpauthUrl = `otpauth://totp/${encodeURIComponent(
    `${ISSUER}:${userName ?? ''}`
  )}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`;

  const submit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (!cognitoUser) {
      return;
    }
    setErr('');
    if (!MFA_CODE_PATTERN.test(code.trim())) {
      setErr(MFA_CODE_FORMAT_ERROR);

      return;
    }
    setDisable(true);
    enableTotp(cognitoUser, code.trim())
      .then(() => {
        setOpen(true);
      })
      .catch(error => {
        setErr(mfaErrorhandle(error));
        setDisable(false);
      });
  };

  return (
    <>
      <ComfirmdDalog
        title="2段階認証(MFA)設定"
        content="2段階認証(MFA)の設定が完了しました。次回ログインから認証アプリのコードの入力が必要になります。"
        open={open}
        buttonText="OK"
        onClickFunc={() => {
          setOpen(false);
          history.push(`${URL.settings}?account`);
        }}
      />
      <Container css={segmntMargin} text>
        <Segment color="blue" css={segmentWidth} style={{ margin: '0 auto' }}>
          <SegmentHeader rightText="ログイン時の2段階認証を設定します">
            <p css={titleNowrap}>2段階認証(MFA)設定</p>
          </SegmentHeader>
          {err && <ErrorMassage errMessage={err} />}
          {loading && <Loading />}
          {!loading && alreadyEnabled && (
            <Form css={buttonBottom}>
              <SettingText>
                <p>
                  すでに2段階認証(MFA)が設定されています。再設定する場合は、アカウント管理で解除してから設定してください。
                </p>
              </SettingText>
              <Form.Field>
                <DefaultButton
                  text="アカウント管理へ戻る"
                  marginTp="20px"
                  width="30%"
                  onClinckFunc={() => history.push(`${URL.settings}?account`)}
                />
              </Form.Field>
            </Form>
          )}
          {!loading && secret && (
            <Form css={buttonBottom}>
              <SettingText>
                <p>
                  1. Google Authenticator
                  等の認証アプリで、下記のQRコードを読み取ってください。
                </p>
              </SettingText>
              <div css={qrStyle}>
                <QRCodeSVG value={otpauthUrl} size={180} />
                <p css={secretStyle}>
                  QRコードを読み取れない場合は、次のキーを入力してください。
                  <br />
                  <code>{secret}</code>
                </p>
              </div>
              <SettingText>
                <p>2. 認証アプリに表示された6桁のコードを入力してください。</p>
              </SettingText>
              <DefaultFormInput
                labelText="認証アプリのコード"
                placeholder="123456"
                value={code}
                setValue={setCode}
                error={false}
                errorContent=""
                required
                maxLength={6}
              />
              <Form.Field>
                <DefaultButton
                  text="設定する"
                  marginTp="20px"
                  width="30%"
                  onClinckFunc={e => submit(e)}
                  disabled={disable || code.trim() === ''}
                  accountCancelButton
                />
              </Form.Field>
            </Form>
          )}
        </Segment>
      </Container>
    </>
  );
};

const segmntMargin = css({
  marginTop: '30px',
});

const buttonBottom = css({
  marginTop: '10px',
  marginBottom: '40px',
});

// SegmentHeaderの左カラム幅(4/16)ではタイトルが折り返されるため折り返しを抑止
const titleNowrap = css({
  whiteSpace: 'nowrap',
});

const segmentWidth = css({
  maxWidth: '800px',
});

const qrStyle = css({
  textAlign: 'center',
  margin: '20px 0',
});

const secretStyle = css({
  marginTop: '10px',
  fontSize: '12px',
  wordBreak: 'break-all',
});

export default MfaSetupForm;
